<template>
  <!-- Loader -->
  <div v-if="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
    <div class="text-center">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">Chargement...</span>
      </div>
      <div class="mt-3">
        <h5 class="text-muted">Chargement des statistiques...</h5>
        <p class="text-muted">Veuillez patienter pendant que nous récupérons les données.</p>
      </div>
    </div>
  </div>

  <!-- Contenu principal -->
  <div v-else class="row">
    <!-- Statistiques générales -->
    <div class="col-xl-3 col-md-6">
      <div class="card card-animate">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 overflow-hidden">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0">Total Usagers</p>
            </div>
            <div class="flex-shrink-0">
              <h5 class="text-success fs-14 mb-0">
                <i class="ri-arrow-right-up-line fs-13 align-middle"></i>
                {{ stats.totalUsagers }}
              </h5>
            </div>
          </div>
          <div class="d-flex align-items-end justify-content-between mt-4">
            <div>
              <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                <span class="counter-value" :data-target="stats.totalUsagers">{{ stats.totalUsagers }}</span>
              </h4>
              <span class="badge bg-success-subtle text-success mb-0">
                <i class="ri-arrow-up-line align-middle"></i> Actifs
              </span>
            </div>
            <div class="avatar-sm flex-shrink-0">
              <span class="avatar-title bg-success-subtle rounded fs-3">
                <i class="bx bx-user text-success"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-xl-3 col-md-6">
      <div class="card card-animate">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 overflow-hidden">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0">Total Emplacements</p>
            </div>
            <div class="flex-shrink-0">
              <h5 class="text-info fs-14 mb-0">
                <i class="ri-arrow-right-up-line fs-13 align-middle"></i>
                {{ stats.totalEmplacements }}
              </h5>
            </div>
          </div>
          <div class="d-flex align-items-end justify-content-between mt-4">
            <div>
              <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                <span class="counter-value" :data-target="stats.totalEmplacements">{{ stats.totalEmplacements }}</span>
              </h4>
              <span class="badge bg-info-subtle text-info mb-0">
                <i class="ri-arrow-up-line align-middle"></i> Occupés
              </span>
            </div>
            <div class="avatar-sm flex-shrink-0">
              <span class="avatar-title bg-info-subtle rounded fs-3">
                <i class="bx bx-building text-info"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-xl-3 col-md-6">
      <div class="card card-animate">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 overflow-hidden">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0">Arrièrés non payés</p>
            </div>
            <div class="flex-shrink-0">
              <h5 class="text-warning fs-14 mb-0">
                <i class="ri-arrow-right-up-line fs-13 align-middle"></i>
                {{ formatCurrency(stats.arrriersTotal) }}
              </h5>
            </div>
          </div>
          <div class="d-flex align-items-end justify-content-between mt-4">
            <div>
              <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                <span class="counter-value" :data-target="stats.arrriersTotal">{{ formatCurrency(stats.arrriersTotal) }}</span>
              </h4>
              <span class="badge bg-warning-subtle text-warning mb-0">
                <i class="ri-arrow-up-line align-middle"></i> En attente
              </span>
            </div>
            <div class="avatar-sm flex-shrink-0">
              <span class="avatar-title bg-warning-subtle rounded fs-3">
                <i class="bx bx-money text-warning"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-xl-3 col-md-6">
      <div class="card card-animate">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 overflow-hidden">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0">Revenus Totaux</p>
            </div>
            <div class="flex-shrink-0">
              <h5 class="text-danger fs-14 mb-0">
                <i class="ri-arrow-right-up-line fs-13 align-middle"></i>
                {{ formatCurrency(stats.revenusTotaux) }}
              </h5>
            </div>
          </div>
          <div class="d-flex align-items-end justify-content-between mt-4">
            <div>
              <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                <span class="counter-value" :data-target="stats.revenusTotaux">{{ formatCurrency(stats.revenusTotaux) }}</span>
              </h4>
              <span class="badge bg-danger-subtle text-danger mb-0">
                <i class="ri-arrow-up-line align-middle"></i> Cumulé
              </span>
            </div>
            <div class="avatar-sm flex-shrink-0">
              <span class="avatar-title bg-danger-subtle rounded fs-3">
                <i class="bx bx-trending-up text-danger"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Graphiques -->
    <div class="col-xl-8">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Évolution des Paiements (12 derniers mois)</h4>
        </div>
        <div class="card-body">
          <canvas ref="paiementsChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <div class="col-xl-4">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Répartition par Secteur</h4>
        </div>
        <div class="card-body">
          <canvas ref="secteursChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <div class="col-xl-6">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Statut des Usagers</h4>
        </div>
        <div class="card-body">
          <canvas ref="statutUsagersChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <div class="col-xl-6">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Top 5 Secteurs</h4>
        </div>
        <div class="card-body">
          <canvas ref="activitesChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <div class="col-xl-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Répartition Géographique par Marché</h4>
        </div>
        <div class="card-body">
          <canvas ref="marchesChart" height="200"></canvas>
        </div>
      </div>
    </div>

    <!-- Nouveaux graphiques pour les paiements -->
    <div class="col-xl-6">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Paiements locatifs par Mois</h4>
        </div>
        <div class="card-body">
          <canvas ref="paiementsUsagersChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <div class="col-xl-6">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Paiements autres recettes par Mois</h4>
        </div>
        <div class="card-body">
          <canvas ref="paiementsAmbulantsChart" height="300"></canvas>
        </div>
      </div>
    </div>

    <!-- Tableau des statistiques détaillées -->
    <div class="col-xl-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title mb-0">Statistiques Détaillées</h4>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Indicateur</th>
                  <th>Valeur</th>
                  <th>Évolution</th>
                  <th>Tendance</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stat, key) in statsDetail" :key="key">
                  <td>{{ stat.label }}</td>
                  <td>{{ stat.value }}</td>
                  <td>{{ stat.evolution }}%</td>
                  <td>
                    <span :class="stat.tendance === 'positive' ? 'text-success' : 'text-danger'">
                      <i :class="stat.tendance === 'positive' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'"></i>
                      {{ stat.tendance === 'positive' ? 'Hausse' : 'Baisse' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, nextTick } from "vue";
import axios from "axios";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  DoughnutController,
  PieController,
} from 'chart.js';
import ApiService from "@/services/ApiService";

// Enregistrer les composants Chart.js
Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  DoughnutController,
  PieController
);

export default defineComponent({
  name: "StatistiqueEtat",
  setup() {
    const paiementsChart = ref<HTMLCanvasElement>();
    const secteursChart = ref<HTMLCanvasElement>();
    const statutUsagersChart = ref<HTMLCanvasElement>();
    const activitesChart = ref<HTMLCanvasElement>();
    const marchesChart = ref<HTMLCanvasElement>();
    const paiementsUsagersChart = ref<HTMLCanvasElement>();
    const paiementsAmbulantsChart = ref<HTMLCanvasElement>();

    const isLoading = ref(true);

    const stats = ref({
      arrriersTotal:0,
      totalUsagers: 0,
      totalEmplacements: 0,
      paiementsMois: 0,
      revenusTotaux: 0
    });

    const statsDetail = ref([
      {
        label: "Usagers avec NPI",
        value: "0",
        evolution: 0,
        tendance: "positive"
      },
      {
        label: "Usagers avec IFU",
        value: "0",
        evolution: 0,
        tendance: "positive"
      },
      {
        label: "Emplacements occupés",
        value: "0",
        evolution: 0,
        tendance: "positive"
      },
      {
        label: "Taux d'occupation",
        value: "0%",
        evolution: 0,
        tendance: "positive"
      }
    ]);

    const chartData = ref({
      paiementsMensuels: [],
      secteurs: [],
      marches: [],
      statutUsagers: {
        unePlace: 0,
        plusieursPlaces: 0,
        sansEmplacement: 0
      },
      paiementsUsagersParMois: [],
      paiementsAmbulantsParMois: []
    });

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
    };

    const loadStatistics = async () => {
      try {
        isLoading.value = true;
        const response = await ApiService.get('/etats/statistiques-usagers');
        const data = response.data.data;
        console.log("data statistiques usagers ==>", data);
        
        // Mapper les données de l'API vers notre structure
        stats.value = {
          arrriersTotal: data.totalArrieresNonPayes && data.totalArrieresNonPayes.length > 0 ? 
            parseFloat(data.totalArrieresNonPayes[0].montant) : 0,
          totalUsagers: parseInt(data.totalUsagers[0]?.nombre || 0),
          totalEmplacements: data.totalPlaceParSecteur.reduce((sum: number, item: any) => sum + parseInt(item.nombre), 0),
          paiementsMois: data.totalRecetteMensuelle.length > 0 && data.totalRecetteMensuelle[data.totalRecetteMensuelle.length - 1]?.montant ? 
            parseFloat(data.totalRecetteMensuelle[data.totalRecetteMensuelle.length - 1].montant) : 0,
          revenusTotaux: parseFloat(data.totalPaiementUsagerPlaces || 0) + parseFloat(data.totalPaiementUsagerAmbulants || 0)
        };
        
        // Mettre à jour les statistiques détaillées
        statsDetail.value = [
          {
            label: "Usagers actifs",
            value: parseInt(data.totalUsagersActifs || 0).toString(),
            evolution: 0,
            tendance: "positive"
          },
          {
            label: "Usagers sans emplacement",
            value: parseInt(data.totalElementsAucunePlace || 0).toString(),
            evolution: 0,
            tendance: "negative"
          },
          {
            label: "Usagers avec une place",
            value: parseInt(data.totalElementsAvecUneSeulePlace || 0).toString(),
            evolution: 0,
            tendance: "positive"
          },
          {
            label: "Usagers avec plusieurs places",
            value: parseInt(data.totalElementsAvecPlusieursPlace || 0).toString(),
            evolution: 0,
            tendance: "positive"
          }
        ];
        
        // Stocker les données pour les graphiques
        chartData.value = {
          paiementsMensuels: data.totalRecetteMensuelle || [],
          secteurs: data.totalPlaceParSecteur || [],
          marches: data.totalPlaceParMarche || [],
          statutUsagers: {
            unePlace: parseInt(data.totalElementsAvecUneSeulePlace || 0),
            plusieursPlaces: parseInt(data.totalElementsAvecPlusieursPlace || 0),
            sansEmplacement: parseInt(data.totalUsagersSansEmplacement || 0)
          },
          paiementsUsagersParMois: data.totalPaiementUsagerParMois || [],
          paiementsAmbulantsParMois: data.totalPaiementUsagerAmbulantParMois || []
        };
        
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        // Données de démonstration en cas d'erreur
        stats.value = {
          arrriersTotal: 0,
          totalUsagers: 0,
          totalEmplacements: 0,
          paiementsMois: 0,
          revenusTotaux: 0
        };
      } finally {
        isLoading.value = false;
      }
    };

    const createPaiementsChart = () => {
      if (!paiementsChart.value) return;

      const ctx = paiementsChart.value.getContext('2d');
      if (!ctx) return;

      // Préparer les données des paiements mensuels
      const paiementsData = chartData.value.paiementsMensuels || [];
      
      const labels = paiementsData.map((item: any) => {
        const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        return `${mois[item.mois - 1]} ${item.annee}`;
      });
      const data = paiementsData.map((item: any) => parseFloat(item.montant) || 0);

      new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels.length > 0 ? labels : ['Aucune donnée disponible'],
          datasets: [{
            label: 'Paiements (FCFA)',
            data: data.length > 0 ? data : [0],
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Évolution des revenus mensuels'
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value: any) {
                  return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
                }
              }
            }
          }
        }
      });
    };

    const createSecteursChart = () => {
      if (!secteursChart.value) return;

      const ctx = secteursChart.value.getContext('2d');
      if (!ctx) return;

      // Préparer les données des secteurs - prendre les 10 premiers
      const secteursData = chartData.value.secteurs || [];
      const topSecteurs = secteursData.slice(0, 10);
      const labels = topSecteurs.map((item: any) => item.secteur);
      const data = topSecteurs.map((item: any) => parseInt(item.nombre));

      // Calculer le total des autres secteurs
      const autresTotal = secteursData.slice(10).reduce((sum: number, item: any) => sum + parseInt(item.nombre), 0);
      
      // Ajouter "Autres" si il y a plus de 10 secteurs
      if (autresTotal > 0) {
        labels.push('Autres');
        data.push(autresTotal);
      }

      // Couleurs pour les secteurs
      const colors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#9966FF',
        '#FF6384', '#36A2EB'
      ];

      new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels.length > 0 ? labels : ['Secteur A', 'Secteur B', 'Secteur C', 'Secteur D', 'Autres'],
          datasets: [{
            data: data.length > 0 ? data : [30, 25, 20, 15, 10],
            backgroundColor: colors.slice(0, labels.length),
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 10
                }
              }
            }
          }
        }
      });
    };

    const createStatutUsagersChart = () => {
      if (!statutUsagersChart.value) return;

      const ctx = statutUsagersChart.value.getContext('2d');
      if (!ctx) return;

      // Utiliser les données réelles des statuts d'usagers
      const statutData = chartData.value.statutUsagers;
      const labels = ['Avec une place', 'Avec plusieurs places', 'Sans emplacement'];
      const data = [
        statutData.unePlace || 0, // Usagers avec une place
        statutData.plusieursPlaces || 0, // Usagers avec plusieurs places  
        statutData.sansEmplacement || 0 // Usagers sans emplacement
      ];

      new Chart(ctx, {
        type: 'pie',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: [
              '#28a745',
              '#17a2b8',
              '#ffc107'
            ],
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          }
        }
      });
    };

    const createActivitesChart = () => {
      if (!activitesChart.value) return;

      const ctx = activitesChart.value.getContext('2d');
      if (!ctx) return;

      // Utiliser les 5 premiers secteurs pour le graphique des activités
      const secteursData = chartData.value.secteurs || [];
      const topSecteurs = secteursData.slice(0, 5);
      const labels = topSecteurs.map((item: any) => item.secteur.length > 15 ? 
        item.secteur.substring(0, 15) + '...' : item.secteur);
      const data = topSecteurs.map((item: any) => parseInt(item.nombre));

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels.length > 0 ? labels : ['Commerce', 'Restauration', 'Services', 'Artisanat', 'Autres'],
          datasets: [{
            label: 'Nombre d\'emplacements',
            data: data.length > 0 ? data : [45, 38, 32, 28, 15],
            backgroundColor: [
              'rgba(255, 99, 132, 0.8)',
              'rgba(54, 162, 235, 0.8)',
              'rgba(255, 206, 86, 0.8)',
              'rgba(75, 192, 192, 0.8)',
              'rgba(153, 102, 255, 0.8)'
            ],
            borderColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });
    };

    const createMarchesChart = () => {
      if (!marchesChart.value) return;

      const ctx = marchesChart.value.getContext('2d');
      if (!ctx) return;

      // Préparer les données des marchés
      const marchesData = chartData.value.marches || [];
      const labels = marchesData.map((item: any) => item.marche);
      const data = marchesData.map((item: any) => parseInt(item.nombre));

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels.length > 0 ? labels : ['Marché Central', 'Marché de Cocody', 'Marché d\'Adjamé', 'Marché de Yopougon', 'Marché de Marcory'],
          datasets: [{
            label: 'Nombre d\'emplacements',
            data: data.length > 0 ? data : [120, 95, 80, 70, 60],
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });
    };

    const createPaiementsUsagersChart = () => {
      if (!paiementsUsagersChart.value) return;

      const ctx = paiementsUsagersChart.value.getContext('2d');
      if (!ctx) return;

      // Préparer les données des paiements des usagers par mois
      const paiementsData = chartData.value.paiementsUsagersParMois || [];
      
      const labels = paiementsData.map((item: any) => {
        const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        return `${mois[item.mois - 1]} ${item.annee}`;
      });
      const data = paiementsData.map((item: any) => parseFloat(item.montant) || 0);

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels.length > 0 ? labels : ['Aucune donnée disponible'],
          datasets: [{
            label: 'Paiements Usagers (FCFA)',
            data: data.length > 0 ? data : [0],
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Paiements des usagers par mois'
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value: any) {
                  return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
                }
              }
            }
          }
        }
      });
    };

    const createPaiementsAmbulantsChart = () => {
      if (!paiementsAmbulantsChart.value) return;

      const ctx = paiementsAmbulantsChart.value.getContext('2d');
      if (!ctx) return;

      // Préparer les données des paiements ambulants par mois
      const paiementsData = chartData.value.paiementsAmbulantsParMois || [];
      
      const labels = paiementsData.map((item: any) => {
        const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        return `${mois[item.mois - 1]} ${item.annee}`;
      });
      const data = paiementsData.map((item: any) => parseFloat(item.montant) || 0);

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels.length > 0 ? labels : ['Aucune donnée disponible'],
          datasets: [{
            label: 'Paiements Ambulants (FCFA)',
            data: data.length > 0 ? data : [0],
            backgroundColor: 'rgba(255, 99, 132, 0.8)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Paiements ambulants par mois'
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value: any) {
                  return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
                }
              }
            }
          }
        }
      });
    };

    const initializeCharts = async () => {
      await nextTick();
      createPaiementsChart();
      createSecteursChart();
      createStatutUsagersChart();
      createActivitesChart();
      createMarchesChart();
      createPaiementsUsagersChart();
      createPaiementsAmbulantsChart();
    };

    onMounted(async () => {
      await loadStatistics();
      await initializeCharts();
    });

    return {
      paiementsChart,
      secteursChart,
      statutUsagersChart,
      activitesChart,
      marchesChart,
      paiementsUsagersChart,
      paiementsAmbulantsChart,
      stats,
      statsDetail,
      chartData,
      isLoading,
      formatCurrency
    };
  }
});
</script>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}

.counter-value {
  font-weight: 600;
}

.avatar-sm {
  width: 3rem;
  height: 3rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Styles pour le loader */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Animation fade pour le contenu */
.row {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>